'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { cn } from '@/lib/utils';

const Collapsible = React.forwardRef(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.Root 
    ref={ref} 
    data-slot="collapsible" 
    className={cn(
      'bg-gray-50 dark:bg-gray-800',
      'border border-gray-200 dark:border-gray-700',
      'rounded-2xl',
      'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.2)]',
      'overflow-hidden',
      'transition-all duration-200',
      className
    )}
    {...props} 
  />
));
Collapsible.displayName = CollapsiblePrimitive.Root.displayName;
Collapsible.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const CollapsibleTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleTrigger
    ref={ref}
    data-slot="collapsible-trigger"
    className={cn(
      'flex w-full items-center justify-between p-4',
      'font-roboto-condensed font-semibold text-base leading-6',
      'text-gray-900 dark:text-gray-100',
      'hover:bg-gray-100 dark:hover:bg-gray-700',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      'transition-colors duration-200',
      '[&[data-state=open]>svg]:rotate-180',
      className
    )}
    {...props}
  />
));
CollapsibleTrigger.displayName = CollapsiblePrimitive.CollapsibleTrigger.displayName;
CollapsibleTrigger.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const CollapsibleContent = React.forwardRef(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleContent
    ref={ref}
    data-slot="collapsible-content"
    className={cn(
      'overflow-hidden',
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      'border-t border-gray-200 dark:border-gray-700',
      className
    )}
    {...props}
  />
));
CollapsibleContent.displayName = CollapsiblePrimitive.CollapsibleContent.displayName;
CollapsibleContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

// CollapsibleItem component for individual items within the collapsible content
const CollapsibleItem = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="collapsible-item"
    className={cn(
      'px-4 py-3',
      'font-roboto-condensed font-normal text-base leading-6',
      'text-gray-600 dark:text-gray-400',
      'border-b border-gray-200 dark:border-gray-700 last:border-b-0',
      'hover:bg-gray-50 dark:hover:bg-gray-750',
      'transition-colors duration-150',
      className
    )}
    {...props}
  />
));
CollapsibleItem.displayName = 'CollapsibleItem';
CollapsibleItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export { Collapsible, CollapsibleTrigger, CollapsibleContent, CollapsibleItem };
